import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, Trash2, Edit2, ArrowLeft, X, Check, Search, Filter, FileText } from 'lucide-react';
import { listServices, createService, updateService, deleteService, adminVerify } from '../../services/api';
import WysiwygEditor from '../../components/WysiwygEditor';
import toast from 'react-hot-toast';

const serviceCategories = [
  'MPCB', 'DISH', 'DIC', 'CGWA', 'MIDC', 
  'MSEB', 'Labor', 'FairPrice', 'HazardousWaste'
];

const colorOptions = [
  { value: 'from-red-600 via-red-700 to-red-800', label: 'Red', bg: 'bg-red-50' },
  { value: 'from-blue-600 via-blue-700 to-blue-800', label: 'Blue', bg: 'bg-blue-50' },
  { value: 'from-green-600 via-emerald-600 to-teal-600', label: 'Green', bg: 'bg-green-50' },
  { value: 'from-primary-600 via-primary-700 to-primary-800', label: 'Primary Blue', bg: 'bg-primary-50' },
  { value: 'from-slate-600 via-slate-700 to-slate-800', label: 'Slate', bg: 'bg-slate-50' },
  { value: 'from-cyan-400 via-blue-500 to-teal-500', label: 'Cyan', bg: 'bg-cyan-50' },
  { value: 'from-orange-600 via-red-600 to-red-700', label: 'Orange/Red', bg: 'bg-orange-50' },
  { value: 'from-purple-600 via-purple-700 to-purple-800', label: 'Purple', bg: 'bg-purple-50' },
  { value: 'from-pink-600 via-pink-700 to-pink-800', label: 'Pink', bg: 'bg-pink-50' },
  { value: 'from-yellow-500 via-yellow-600 to-yellow-700', label: 'Yellow', bg: 'bg-yellow-50' }
];

const emptyForm = {
  name: { en: '', mr: '', hi: '' },
  category: '',
  description: { en: '', mr: '', hi: '' },
  detailedDescription: { en: '', mr: '', hi: '' },
  benefits: { en: [], mr: [], hi: [] },
  requirements: { en: [], mr: [], hi: [] },
  process: { en: [], mr: [], hi: [] },
  documents: { en: [], mr: [], hi: [] },
  fees: { amount: '', currency: 'INR', description: '' },
  processingTime: '7-15 working days',
  contactInfo: { department: '', email: '', phone: '' },
  color: 'from-primary-600 via-primary-700 to-primary-800',
  bgColor: 'bg-primary-50',
  icon: '',
  image: ''
};

const AdminServicesPage = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    category: ''
  });
  const [activeLang, setActiveLang] = useState('en'); // Language selector for form
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
      } catch (e) {
        navigate('/admin');
      }
    };
    init();
  }, [navigate]);

  async function refresh() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const services = await listServices();
      const sorted = services.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      setAllItems(sorted);
      applyFilters(sorted);
    } catch (e) {
      const errorMsg = e.message || 'Failed to load services';
      setError(errorMsg);
      toast.error(errorMsg);
      setAllItems([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  function applyFilters(data) {
    let filtered = [...data];
    
    if (filters.name) {
      filtered = filtered.filter(item => {
        const name = item.name || {};
        const nameStr = (name.en || name.mr || name.hi || '').toLowerCase();
        return nameStr.includes(filters.name.toLowerCase());
      });
    }
    
    if (filters.category) {
      filtered = filtered.filter(item => item.category === filters.category);
    }
    
    setItems(filtered);
  }

  useEffect(() => {
    applyFilters(allItems);
  }, [filters, allItems]);

  function handleFilterChange(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Validate required fields (at least English)
    if (!form.name?.en || !form.category || !form.description?.en || !form.detailedDescription?.en) {
      setError('Please fill in all required fields (Name (EN), Category, Description (EN), Detailed Description (EN))');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        fees: {
          ...form.fees,
          amount: form.fees.amount ? Number(form.fees.amount) : undefined
        },
        // Filter multilingual arrays
        benefits: {
          en: (form.benefits?.en || []).filter(b => b.trim()),
          mr: (form.benefits?.mr || []).filter(b => b.trim()),
          hi: (form.benefits?.hi || []).filter(b => b.trim())
        },
        requirements: {
          en: (form.requirements?.en || []).filter(r => r.trim()),
          mr: (form.requirements?.mr || []).filter(r => r.trim()),
          hi: (form.requirements?.hi || []).filter(r => r.trim())
        },
        process: {
          en: (form.process?.en || []).filter(p => p.step && p.description),
          mr: (form.process?.mr || []).filter(p => p.step && p.description),
          hi: (form.process?.hi || []).filter(p => p.step && p.description)
        },
        documents: {
          en: (form.documents?.en || []).filter(d => d.name || d.description),
          mr: (form.documents?.mr || []).filter(d => d.name || d.description),
          hi: (form.documents?.hi || []).filter(d => d.name || d.description)
        }
      };
      
      if (editingId) {
        await updateService(editingId, payload);
        setSuccess('Service updated successfully!');
        toast.success('Service updated successfully!');
      } else {
        await createService(payload);
        setSuccess('Service created successfully!');
        toast.success('Service created successfully!');
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Save failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) return;
    try {
      await deleteService(id);
      setSuccess('Service deleted successfully!');
      toast.success('Service deleted successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Delete failed';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  }

  function startEdit(item) {
    setEditingId(item._id);
    // Handle both old format (string) and new format (multilingual object)
    const name = typeof item.name === 'string' 
      ? { en: item.name, mr: '', hi: '' } 
      : (item.name || { en: '', mr: '', hi: '' });
    const description = typeof item.description === 'string'
      ? { en: item.description, mr: '', hi: '' }
      : (item.description || { en: '', mr: '', hi: '' });
    const detailedDescription = typeof item.detailedDescription === 'string'
      ? { en: item.detailedDescription, mr: '', hi: '' }
      : (item.detailedDescription || { en: '', mr: '', hi: '' });
    const benefits = Array.isArray(item.benefits)
      ? { en: item.benefits, mr: [], hi: [] }
      : (item.benefits || { en: [], mr: [], hi: [] });
    const requirements = Array.isArray(item.requirements)
      ? { en: item.requirements, mr: [], hi: [] }
      : (item.requirements || { en: [], mr: [], hi: [] });
    const process = Array.isArray(item.process)
      ? { en: item.process, mr: [], hi: [] }
      : (item.process || { en: [], mr: [], hi: [] });
    const documents = Array.isArray(item.documents)
      ? { en: item.documents, mr: [], hi: [] }
      : (item.documents || { en: [], mr: [], hi: [] });
    
    setForm({
      name,
      category: item.category || '',
      description,
      detailedDescription,
      benefits,
      requirements,
      process,
      documents,
      fees: item.fees || { amount: '', currency: 'INR', description: '' },
      processingTime: item.processingTime || '7-15 working days',
      contactInfo: item.contactInfo || { department: '', email: '', phone: '' },
      color: item.color || 'from-primary-600 via-primary-700 to-primary-800',
      bgColor: item.bgColor || 'bg-primary-50',
      icon: item.icon || '',
      image: item.image || ''
    });
    setShowForm(true);
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(false);
    setError('');
    setSuccess('');
  }

  function handleNew() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Multilingual array helpers
  function addArrayItem(field, lang, value = '') {
    setForm(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] || { en: [], mr: [], hi: [] }),
        [lang]: [...(prev[field]?.[lang] || []), value]
      }
    }));
  }

  function updateArrayItem(field, lang, index, value) {
    setForm(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] || { en: [], mr: [], hi: [] }),
        [lang]: (prev[field]?.[lang] || []).map((item, i) => i === index ? value : item)
      }
    }));
  }

  function removeArrayItem(field, lang, index) {
    setForm(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] || { en: [], mr: [], hi: [] }),
        [lang]: (prev[field]?.[lang] || []).filter((_, i) => i !== index)
      }
    }));
  }

  function addProcessItem(lang) {
    setForm(prev => {
      const currentProcess = prev.process?.[lang] || [];
      return {
        ...prev,
        process: {
          ...(prev.process || { en: [], mr: [], hi: [] }),
          [lang]: [...currentProcess, { step: currentProcess.length + 1, description: '' }]
        }
      };
    });
  }

  function updateProcessItem(lang, index, field, value) {
    setForm(prev => ({
      ...prev,
      process: {
        ...(prev.process || { en: [], mr: [], hi: [] }),
        [lang]: (prev.process?.[lang] || []).map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  }

  function addDocumentItem(lang) {
    setForm(prev => ({
      ...prev,
      documents: {
        ...(prev.documents || { en: [], mr: [], hi: [] }),
        [lang]: [...(prev.documents?.[lang] || []), { name: '', description: '', required: false }]
      }
    }));
  }

  function updateDocumentItem(lang, index, field, value) {
    setForm(prev => ({
      ...prev,
      documents: {
        ...(prev.documents || { en: [], mr: [], hi: [] }),
        [lang]: (prev.documents?.[lang] || []).map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Services</h1>
              <p className="text-xs text-gray-500">Create and manage services</p>
            </div>
          </div>
          {!showForm && (
            <button
              onClick={handleNew}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-2.5 rounded-lg hover:from-primary-700 hover:to-primary-800 hover:text-black transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Plus className="h-5 w-5" />
              Create New Service
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{error}</span>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white shadow-xl rounded-2xl p-8 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingId ? 'Edit Service' : 'Create New Service'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {editingId ? 'Update the service details below' : 'Fill in the form below to create a new service'}
                </p>
              </div>
              <button
                onClick={resetForm}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Close form"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Language Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex gap-2">
                {['en', 'mr', 'hi'].map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setActiveLang(lang)}
                    className={`px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors ${
                      activeLang === lang
                        ? 'bg-primary-600 text-white border-b-2 border-primary-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {lang === 'en' ? 'English' : lang === 'mr' ? 'Marathi' : 'Hindi'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Name ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'}) 
                  {activeLang === 'en' && <span className="text-red-500">*</span>}
                </label>
                <input 
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white" 
                  value={form.name?.[activeLang] || ''} 
                  onChange={e => setForm({ 
                    ...form, 
                    name: { ...form.name, [activeLang]: e.target.value }
                  })} 
                  placeholder={`Enter service name in ${activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'}`}
                  required={activeLang === 'en'}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white text-gray-900"
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {serviceCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Description ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'})
                {activeLang === 'en' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                value={form.description?.[activeLang] || ''}
                onChange={e => setForm({ 
                  ...form, 
                  description: { ...form.description, [activeLang]: e.target.value }
                })}
                placeholder={`Brief description of the service in ${activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'}`}
                rows={3}
                required={activeLang === 'en'}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Detailed Description ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'})
                {activeLang === 'en' && <span className="text-red-500">*</span>}
              </label>
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent transition-all bg-white">
                <WysiwygEditor
                  value={form.detailedDescription?.[activeLang] || ''} 
                  onChange={(content) => setForm({ 
                    ...form, 
                    detailedDescription: { ...form.detailedDescription, [activeLang]: content }
                  })}
                  placeholder={`Enter detailed service description in ${activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'}...`}
                  height="300px"
                />
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Benefits ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'})
              </label>
              {(form.benefits?.[activeLang] || []).map((benefit, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={benefit}
                    onChange={e => updateArrayItem('benefits', activeLang, index, e.target.value)}
                    placeholder={`Benefit ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('benefits', activeLang, index)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('benefits', activeLang)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Benefit
              </button>
            </div>

            {/* Requirements */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Requirements ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'})
              </label>
              {(form.requirements?.[activeLang] || []).map((req, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={req}
                    onChange={e => updateArrayItem('requirements', activeLang, index, e.target.value)}
                    placeholder={`Requirement ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('requirements', activeLang, index)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('requirements', activeLang)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Requirement
              </button>
            </div>

            {/* Process */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Process Steps ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'})
              </label>
              {(form.process?.[activeLang] || []).map((step, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="number"
                    className="w-20 border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={step.step}
                    onChange={e => updateProcessItem(activeLang, index, 'step', Number(e.target.value))}
                    placeholder="Step"
                  />
                  <input
                    className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={step.description}
                    onChange={e => updateProcessItem(activeLang, index, 'description', e.target.value)}
                    placeholder={`Step ${step.step} description`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const currentProcess = form.process?.[activeLang] || [];
                      setForm(prev => ({
                        ...prev,
                        process: {
                          ...prev.process,
                          [activeLang]: currentProcess.filter((_, i) => i !== index)
                        }
                      }));
                    }}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addProcessItem(activeLang)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Process Step
              </button>
            </div>

            {/* Documents */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Documents ({activeLang === 'en' ? 'English' : activeLang === 'mr' ? 'Marathi' : 'Hindi'})
              </label>
              {(form.documents?.[activeLang] || []).map((doc, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      className="border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={doc.name || ''}
                      onChange={e => updateDocumentItem(activeLang, index, 'name', e.target.value)}
                      placeholder="Document name"
                    />
                    <input
                      className="border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={doc.description || ''}
                      onChange={e => updateDocumentItem(activeLang, index, 'description', e.target.value)}
                      placeholder="Description"
                    />
                    <label className="flex items-center gap-2 px-4 py-2 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={doc.required || false}
                        onChange={e => updateDocumentItem(activeLang, index, 'required', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">Required</span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const currentDocs = form.documents?.[activeLang] || [];
                      setForm(prev => ({
                        ...prev,
                        documents: {
                          ...prev.documents,
                          [activeLang]: currentDocs.filter((_, i) => i !== index)
                        }
                      }));
                    }}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addDocumentItem(activeLang)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Document
              </button>
            </div>

            {/* Fees */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Fee Amount</label>
                <input
                  type="number"
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={form.fees.amount}
                  onChange={e => setForm({ ...form, fees: { ...form.fees, amount: e.target.value } })}
                  placeholder="Amount"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Currency</label>
                <select
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={form.fees.currency}
                  onChange={e => setForm({ ...form, fees: { ...form.fees, currency: e.target.value } })}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Fee Description</label>
                <input
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={form.fees.description}
                  onChange={e => setForm({ ...form, fees: { ...form.fees, description: e.target.value } })}
                  placeholder="Fee description"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Processing Time</label>
              <input
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={form.processingTime}
                onChange={e => setForm({ ...form, processingTime: e.target.value })}
                placeholder="e.g., 7-15 working days"
              />
            </div>

            {/* Color Selection */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900">Service Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Gradient Color <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.color}
                    onChange={e => {
                      const selected = colorOptions.find(opt => opt.value === e.target.value);
                      setForm({ 
                        ...form, 
                        color: e.target.value,
                        bgColor: selected ? selected.bg : form.bgColor
                      });
                    }}
                    required
                  >
                    {colorOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <div className={`h-12 rounded-lg bg-gradient-to-br ${form.color} border-2 border-gray-300`}></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Background Color <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.bgColor}
                    onChange={e => setForm({ ...form, bgColor: e.target.value })}
                    required
                  >
                    {colorOptions.map(option => (
                      <option key={option.bg} value={option.bg}>
                        {option.label} Background
                      </option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <div className={`h-12 rounded-lg ${form.bgColor} border-2 border-gray-300`}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Department</label>
                  <input
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.contactInfo.department}
                    onChange={e => setForm({ ...form, contactInfo: { ...form.contactInfo, department: e.target.value } })}
                    placeholder="Department"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Email</label>
                  <input
                    type="email"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.contactInfo.email}
                    onChange={e => setForm({ ...form, contactInfo: { ...form.contactInfo, email: e.target.value } })}
                    placeholder="Email"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Phone</label>
                  <input
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.contactInfo.phone}
                    onChange={e => setForm({ ...form, contactInfo: { ...form.contactInfo, phone: e.target.value } })}
                    placeholder="Phone"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
              <button 
                type="submit" 
                disabled={saving} 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-3 rounded-lg hover:from-primary-700 hover:to-primary-800 hover:text-black disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg font-semibold"
              >
                <Save className="h-5 w-5" /> 
                {saving ? 'Loading...' : editingId ? 'Update' : 'Create'}
              </button>
              <button 
                type="button" 
                onClick={resetForm} 
                className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Name</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.name ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search name..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
                {filters.name && (
                  <button
                    onClick={() => handleFilterChange('name', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Category</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {serviceCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Services List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-gray-600">Loading services...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No services found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {items.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 transition-all hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {typeof item.name === 'string' 
                          ? item.name 
                          : (item.name?.en || item.name?.mr || item.name?.hi || 'Service')}
                      </h3>
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                        {item.category}
                      </span>
                      {item.color && (
                        <div className={`h-6 w-6 rounded-full bg-gradient-to-br ${item.color} border border-gray-300`} title="Service Color"></div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {typeof item.description === 'string' 
                        ? item.description 
                        : (item.description?.en || item.description?.mr || item.description?.hi || 'No description')}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Created: {new Date(item.createdAt).toLocaleDateString()}</span>
                      {item.updatedAt && item.updatedAt !== item.createdAt && (
                        <span>Updated: {new Date(item.updatedAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => startEdit(item)}
                      className="p-2 rounded-lg bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminServicesPage;

